import React from "react";
import {
  Box,
  FormControl,
  Select,
  Flex,
  Text,
  Checkbox,
  VStack,
} from "@chakra-ui/react";
import { Formik, Form } from "formik";
import { emailProviders } from "../../_helpers/data_helper";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";
import EmailProviderMapping from "./EmailProviderMapping";
import { useToast } from "@chakra-ui/react";
import { saveEmailChannels, updateEmailChannel } from "../../Services/api";

const Transactional = ({ onSave, editingChannel }) => {
  const toast = useToast();

  const credentialTemplates = {
    GenericSMTP: { host: "", port: "", backend: "SMTP", username: "", name: "", password: "", from: "", replyTo: "", security: "" },
    Netcore: { apiKey: "", from: "", backend: "Netcore", name: "", replyTo: "" },
    SendGrid: { apiKey: "", from: "", backend: "SendGrid", name: "", replyTo: "" },
    Mandrill: { from: "", key: "", backend: "Mandrill", name: "", replyTo: "" },
    SparkPost: { apiKey: "", from: "", backend: "SparkPost", name: "", replyTo: "" },
    AmazonSES: { apiKey: "", senderId: "", backend: "AmazonSes", name: "", fromEmail: "", region: "", secretKey: "", replyTo: "" },
    DiskWriter: { localPath: "", backend: "DiskWriter", name: "" },
    Equitas: { name: "", channelID: "", transactionType: "", backend: "Equitas", transactionSubType: "", conversationID: "", externalReferenceId: "", isAsync: false },
  };

  const getInitialData = () => {
    if (!editingChannel) {
      const defaultProvider = emailProviders[0].key;
      return {
        provider: defaultProvider,
        credentials: { ...credentialTemplates[defaultProvider], name: "" },
      };
    }

    const creds = editingChannel.credentials || {};
    const backend = creds.backend || "Netcore";
    let providerKey = "GenericSMTP";

    const matched = emailProviders.find(p => p.key.toLowerCase() === backend.toLowerCase());
    if (matched) providerKey = matched.key;

    return {
      provider: providerKey,
      credentials: {
        ...credentialTemplates[providerKey],
        ...creds,
        name: editingChannel.name || creds.name || "",
      },
    };
  };

  const { provider: initProvider, credentials: initCreds } = getInitialData();

  const [selectedGateway, setSelectedGateway] = React.useState(initProvider);
  const [emailType, setEmailType] = React.useState({
    transactional: true,
    promotional: true,
  });
  const [loading, setLoading] = React.useState(false);

  const EmailComponent = EmailProviderMapping[selectedGateway];

  const initialValues = {
    provider: initProvider,
    credentials: initCreds,
  };

  const handleGatewayChange = (value, setFieldValue) => {
    setSelectedGateway(value);
    setFieldValue("credentials", {
      ...credentialTemplates[value],
      name: initialValues.credentials.name, // preserve name on switch
    });
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const payload = {
        name: values.credentials.name,
        credentials: {
          ...values.credentials,
          backend: values.credentials.backend || values.provider,
          port: values.credentials.port ? Number(values.credentials.port) : undefined,
        },
        extraData: { test: false },
        setDefaultTransactional: emailType.transactional,
        setDefaultPromotional: emailType.promotional,
      };

      if (editingChannel?.uuid) {
        payload.uuid = editingChannel.uuid;
        await updateEmailChannel(payload)
      }else{

        await saveEmailChannels(payload);
      }


      toast({
        title: "Success",
        description: editingChannel ? "Configuration updated." : "Configuration saved.",
        status: "success",
      });

      onSave();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to save.",
        status: "error",
      });
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ setFieldValue, isSubmitting }) => (
        <Form>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <Text fontWeight="bold" mb={2}>Email Gateway</Text>
              <Select
                value={selectedGateway}
                onChange={(e) => handleGatewayChange(e.target.value, setFieldValue)}
              >
                {emailProviders.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <Text fontWeight="bold" mb={3}>Set as Default For</Text>
              <Flex gap={8}>
                <Checkbox
                  isChecked={emailType.transactional}
                  onChange={() => setEmailType(prev => ({ ...prev, transactional: !prev.transactional }))}
                >
                  Default Transactional
                </Checkbox>
                <Checkbox
                  isChecked={emailType.promotional}
                  onChange={() => setEmailType(prev => ({ ...prev, promotional: !prev.promotional }))}
                >
                  Default Promotional
                </Checkbox>
              </Flex>
            </FormControl>

            {EmailComponent && <EmailComponent />}

            <Flex justify="flex-end">
              <PrimaryButton
                label={editingChannel ? "Update" : "Save"}
                type="submit"
                isLoading={isSubmitting || loading}
              />
            </Flex>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export default Transactional;